/* eslint-disable camelcase */
// node modules
import React, { useState } from 'react';
import useStyles from 'isomorphic-style-loader/useStyles';
import { useSelector } from 'react-redux';
import { gql, useQuery, useMutation } from '@apollo/client';
import get from 'lodash/get';
import PulseLoader from 'react-spinners/PulseLoader';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// Material UI
import Button from '@material-ui/core/Button';
import CloseIcon from '@material-ui/icons/Close';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormLabel from '@material-ui/core/FormLabel';
import IconButton from '@material-ui/core/IconButton';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Rating from '@material-ui/lab/Rating';

// app: components
import Icon from 'components/Icon';

// app modules: constants
import { COLOURS } from 'constants/index';

// app modules: services
import history from 'services/history';

// assets
import CssModal from 'components/Modal/Modal.scss';
import CssUserRecipeFeedback from './UserRecipeFeedback.scss';

const GET_USER_FEEDBACK = gql`
  query GetUserRecipeFeedback($content_id: String!) {
    UserRecipeFeedback(content_id: $content_id) {
      id
      did_cook
      star_rating
      difficulty
    }
  }
`;

const SAVE_FEEDBACK = gql`
  mutation SaveRecipeFeedback(
    $content_id: String!
    $content_hash: String
    $did_cook: Boolean
    $star_rating: Float
    $difficulty: Int
  ) {
    UserRecipeFeedback(
      content_id: $content_id
      content_hash: $content_hash
      did_cook: $did_cook
      star_rating: $star_rating
      difficulty: $difficulty
    ) {
      id
      star_rating
      difficulty
      did_cook
      ratings {
        average
        count
      }
    }
  }
`;

const UserRecipeFeedback = props => {
  const { contentId, hash, className, previewOnly } = props;
  useStyles(CssUserRecipeFeedback, CssModal);
  const [open, setOpen] = useState(false);
  const [difficulty, setDifficulty] = useState(null);
  const [starRating, setStarRating] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const displayType = useSelector(state => state.dimensions.displayType);
  const loggedIn = useSelector(state => state.user.logged_in);

  const { data } = useQuery(GET_USER_FEEDBACK, {
    variables: {
      content_id: contentId,
      _authenticated: true,
    },
    ssr: false,
    skip: !loggedIn || previewOnly,
  });

  const [saveFeedback] = useMutation(SAVE_FEEDBACK, {
    update(
      cache,
      {
        data: { UserRecipeFeedback: mutationResponse },
      },
    ) {
      if (mutationResponse.ratings) {
        // Update the recipe cache with the new aggregate ratings
        cache.writeFragment({
          data: { ratings: mutationResponse.ratings },
          fragment: gql`
            fragment RecipeRating on Recipe {
              ratings {
                average
                count
              }
            }
          `,
          id: `Recipe:${contentId}:full`,
        });
        cache.writeFragment({
          data: { ratings: mutationResponse.ratings },
          fragment: gql`
            fragment RecipeRating on Recipe {
              ratings {
                average
                count
              }
            }
          `,
          id: `Recipe:${contentId}:preview`,
        });
      }
      const resp = cache.writeQuery({
        data: {
          UserRecipeFeedback: mutationResponse,
        },
        query: GET_USER_FEEDBACK,
        variables: {
          content_id: contentId,
        },
      });
      return resp;
    },
  });

  const didCook = get(data, 'UserRecipeFeedback.did_cook', false);
  const existingStarRating = get(data, 'UserRecipeFeedback.star_rating', null);
  const existingDifficulty = get(data, 'UserRecipeFeedback.difficulty', null);

  const handleCookedThisButtonClick = () => {
    if (!loggedIn) {
      history.push('/subscribe', {
        returnTo: history.location,
        signupMessage: 'Join ckbk free to rate this recipe',
      });
      return;
    }

    if (!didCook) {
      setOpen(true);
    }
    saveFeedback({
      variables: {
        content_hash: hash,
        content_id: contentId,
        did_cook: !didCook,
      },
      optimisticResponse: {
        UserRecipeFeedback: {
          id: get(data, 'UserRecipeFeedback.id', 'temp-id'),
          __typename: 'UserRecipeFeedback',
          did_cook: !didCook,
          star_rating: existingStarRating,
          difficulty: existingDifficulty,
          ratings: null,
        },
      },
    });
  };

  const handleClose = () => {
    setOpen(false);
    setDifficulty(null);
    setStarRating(null);
    setSubmitting(false);
  };

  const handleDifficultyChange = (event, value) => {
    setDifficulty(parseInt(value, 10));
  };

  const handleRatingChange = (event, value) => {
    setStarRating(value);
  };

  const handleSubmit = async event => {
    event.preventDefault();
    await saveFeedback({
      variables: {
        content_hash: hash,
        content_id: contentId,
        star_rating: starRating,
        difficulty,
      },
    });
    handleClose();
  };

  const handleAddReviewClick = () => {
    history.replace({ hash: '#review' });
    setOpen(false);
  };

  let buttonText;
  if (displayType === 'mobile') {
    if (didCook) {
      buttonText = 'Cooked';
    } else {
      buttonText = 'Cooked?';
    }
  } else {
    buttonText = 'I cooked this';
  }
  return (
    <div className={`user-recipe-feedback ${className}`}>
      <Button
        size="medium"
        color="primary"
        onClick={handleCookedThisButtonClick}
        variant={didCook ? 'contained' : 'text'}
        startIcon={
          <Icon
            fill={didCook ? COLOURS.WHITE : COLOURS.ENERGIZED}
            name="chefshat"
            height="20"
            width="20"
          />
        }
        className={classNames('user-recipe-feedback__button', {
          'user-recipe-feedback__button--on': didCook,
          'user-recipe-feedback__button--off': !didCook,
        })}
      >
        {buttonText}
      </Button>
      <Dialog open={open} onClose={handleClose} aria-labelledby="i-cooked-this-title">
        <DialogTitle disableTypography className="modal__header">
          <h3 className="modal__heading" id="i-cooked-this-title">
            Recipe Feedback
          </h3>
          <IconButton
            className="user-recipe-feedback__close"
            aria-label="close"
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <form className="user-recipe-feedback__form" onSubmit={handleSubmit}>
            <FormControl component="fieldset" className="user-recipe-feedback__control">
              <FormLabel required className="user-recipe-feedback__label">
                How would you rate this recipe?
              </FormLabel>
              <div>
                <Rating
                  value={starRating || existingStarRating}
                  name="i-cooked-this-star-rating"
                  precision={1}
                  size="large"
                  onChange={handleRatingChange}
                />
              </div>
            </FormControl>
            <FormControl component="fieldset" className="user-recipe-feedback__control">
              <FormLabel className="user-recipe-feedback__label">
                How would you rate the difficulty of this recipe?
              </FormLabel>
              <RadioGroup
                row
                aria-label="difficulty"
                name="difficulty"
                className="user-recipe-feedback__difficulty-group"
                value={difficulty || existingDifficulty}
                onChange={handleDifficultyChange}
              >
                <FormControlLabel
                  value={1}
                  className="user-recipe-feedback__recipe-difficulty"
                  control={<Radio color="primary" />}
                  label="Easy"
                  labelPlacement="bottom"
                />
                <FormControlLabel
                  value={2}
                  className="user-recipe-feedback__recipe-difficulty"
                  control={<Radio color="primary" />}
                  label="Medium"
                  labelPlacement="bottom"
                />
                <FormControlLabel
                  value={3}
                  className="user-recipe-feedback__recipe-difficulty"
                  control={<Radio color="primary" />}
                  label="Complex"
                  labelPlacement="bottom"
                />
              </RadioGroup>
            </FormControl>
            <DialogActions className="user-recipe-feedback__actions">
              <Button
                size="large"
                type="submit"
                variant="outlined"
                color="primary"
                disabled={submitting || (!starRating && !existingStarRating)}
              >
                {submitting ? <PulseLoader loading color="#fff" sizeUnit="px" size={10} /> : 'Done'}
              </Button>
              <Button
                size="large"
                type="submit"
                variant="contained"
                color="primary"
                onClick={handleAddReviewClick}
                disabled={submitting || (!starRating && !existingStarRating)}
              >
                {submitting ? (
                  <PulseLoader loading color="#fff" sizeUnit="px" size={10} />
                ) : (
                  'Write a review'
                )}
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

UserRecipeFeedback.propTypes = {
  className: PropTypes.string,
  contentId: PropTypes.string.isRequired,
  hash: PropTypes.string,
  previewOnly: PropTypes.bool,
};

UserRecipeFeedback.defaultProps = {
  hash: null,
  className: '',
  previewOnly: false,
};

UserRecipeFeedback.whyDidYouRender = false;

export default React.memo(UserRecipeFeedback);
